import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { LuArrowRight, LuCheck, LuPlus, LuX } from 'react-icons/lu';
import { Box } from '@/components/ui/lib/box';
import { Button } from '@/components/ui/lib/button';
import { Dialog } from '@/components/ui/lib/dialog';
import { Flex } from '@/components/ui/lib/flex';
import { IconButton } from '@/components/ui/lib/icon-button';
import { Portal } from '@/components/ui/lib/portal';
import { Text } from '@/components/ui/lib/text';
import WorkspaceAvatar from '@/components/ui/workspace-avatar';
import { APP_PATHS } from '@/config/routes/app-paths';
import { isDashboardNavPathAccessible } from '@/config/routes/dashboard-paths';
import { DICTIONARY } from '@/config/dictionary';
import { useWorkspaceLogos } from '@/features/wholesaler-setup/api/overview-api';
import { useSetupStore } from '@/features/wholesaler-setup/stores/setup-store';
import { useAppStore } from '@/stores/app-store';
import { ensureValidPath } from '@/utils/validate-path';
import type { WorkspaceSwitcherDialogProps } from '@/app/types/workspace-switcher-dialog';

const WorkspaceSwitcherDialog = ({ open, onClose }: WorkspaceSwitcherDialogProps) => {
  const currentUser = useAppStore((state) => state.currentUser);
  const selectedWholesalerId = useAppStore((state) => state.selectedWholesalerId);
  const setSelectedWholesalerId = useAppStore((state) => state.setSelectedWholesalerId);
  const startNewWholesalerSetup = useSetupStore((state) => state.startNewWholesalerSetup);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const queryClient = useQueryClient();
  const d = DICTIONARY.dashboard.workspaceSwitcher;
  const wholesalers = currentUser?.wholesalers ?? [];
  // One /overview request per row — the only endpoint that exposes a logo. Gated on `open` so
  // the fan-out waits until the rows are on screen.
  const logos = useWorkspaceLogos(
    wholesalers.map((wholesaler) => wholesaler.id),
    open,
  );

  const handleSelect = (wholesalerId: string) => {
    if (selectedWholesalerId != null) {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.some((segment) => segment === selectedWholesalerId),
      });
    }
    setSelectedWholesalerId(wholesalerId);
    // The role in the newly selected workspace may not grant the page we're currently on.
    if (!isDashboardNavPathAccessible(pathname, currentUser, wholesalerId)) {
      void navigate({ to: ensureValidPath(APP_PATHS.overview) });
    }
    onClose();
  };

  const handleAddWholesaler = async () => {
    startNewWholesalerSetup(undefined, false);
    onClose();
    await navigate({ to: ensureValidPath(APP_PATHS.setup) });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      placement="center"
      size="lg"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="560px"
            maxH="85dvh"
            display="flex"
            flexDirection="column"
            rounded="2xl"
            className="bg-surface border-rim shadow-xl"
          >
            <Dialog.Header pb="2" flexShrink="0">
              <Flex align="flex-start" justify="space-between" gap="4" w="full">
                <Box>
                  <Dialog.Title fontSize="2xl" fontWeight="700" className="text-base">
                    {d.title}
                  </Dialog.Title>
                  <Text mt="2" fontSize="sm" className="text-muted">
                    {d.description}
                  </Text>
                </Box>
                <Dialog.CloseTrigger asChild>
                  <IconButton aria-label={d.closeAriaLabel} variant="ghost" size="sm">
                    <LuX />
                  </IconButton>
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Header>

            <Dialog.Body pb="6" display="flex" flexDirection="column" minH="0" overflow="hidden">
              <Flex
                direction="column"
                gap="3"
                flex="1"
                minH="0"
                overflowY="auto"
                pr="2"
                scrollbarWidth="thin"
                scrollbarColor="var(--border-color) transparent"
              >
                {wholesalers.map((wholesaler) => {
                  const isSelected = wholesaler.id === selectedWholesalerId;

                  return (
                    <Button
                      key={wholesaler.id}
                      type="button"
                      variant="ghost"
                      h="auto"
                      flexShrink="0"
                      px="4"
                      py="4"
                      justifyContent="stretch"
                      borderWidth="1px"
                      rounded="xl"
                      onClick={() => {
                        handleSelect(wholesaler.id);
                      }}
                      className={
                        isSelected ? 'border-accent bg-accent/5' : 'border-rim hover:bg-subtle'
                      }
                    >
                      <Flex align="center" gap="4" w="full">
                        <WorkspaceAvatar
                          name={wholesaler.name}
                          logoUrl={logos[wholesaler.id]}
                          size="lg"
                        />
                        <Box flex="1" textAlign="left">
                          <Text fontSize="md" fontWeight="700" className="text-base">
                            {wholesaler.name}
                          </Text>
                          <Text fontSize="sm" className="text-muted">
                            {d.ownerLabel}
                          </Text>
                        </Box>
                        {isSelected ? (
                          <Flex align="center" gap="2" className="text-accent">
                            <LuCheck className="h-4 w-4" />
                            <Text fontSize="sm" fontWeight="600">
                              {d.currentLabel}
                            </Text>
                          </Flex>
                        ) : (
                          <LuArrowRight className="h-4 w-4 text-muted" />
                        )}
                      </Flex>
                    </Button>
                  );
                })}
              </Flex>

              <Box mt="5" pt="5" flexShrink="0" borderTopWidth="1px" className="border-rim">
                <Flex
                  justify={{ base: 'stretch', sm: 'space-between' }}
                  gap="3"
                  direction={{ base: 'column', sm: 'row' }}
                >
                  <Button type="button" variant="outline" onClick={onClose}>
                    {DICTIONARY.common.close}
                  </Button>
                  <Button
                    type="button"
                    colorPalette="sky"
                    onClick={() => {
                      void handleAddWholesaler();
                    }}
                  >
                    <LuPlus />
                    {d.addButton}
                  </Button>
                </Flex>
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default WorkspaceSwitcherDialog;
